import { type ReactNode, useState } from 'react';
import { BiCheck, BiFilterAlt } from 'react-icons/bi';

import { AnchoredPopover } from '@/components/AnchoredPopover/AnchoredPopover';
import { ButtonPill } from '@/components/Button';
import { SegmentedControl, type SegmentedOption } from '@/components/SegmentedControl';

import s from './ExpensesToolbar.module.scss';

export type FilterOption = {
  value: string;
  label: string;
};

export type FilterGroup = {
  key: string;
  title: string;
  options: FilterOption[];
};

type ExpensesToolbarProps<T extends string> = {
  segments?: readonly SegmentedOption<T>[];
  segment?: T;
  onSegmentChange?: (value: T) => void;
  filterGroups: FilterGroup[];
  filterValues: Record<string, string>;
  onFilterChange: (groupKey: string, value: string) => void;
  onClearFilters?: () => void;
  actions?: ReactNode;
};

export const ExpensesToolbar = <T extends string>({
  segments,
  segment,
  onSegmentChange,
  filterGroups,
  filterValues,
  onFilterChange,
  onClearFilters,
  actions,
}: ExpensesToolbarProps<T>) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);

  const hasActiveFilter = filterGroups.some(
    (group) => (filterValues[group.key] ?? 'all') !== 'all',
  );

  return (
    <div className={s.toolbar}>
      {segments && segment !== undefined && onSegmentChange && (
        <SegmentedControl size="sm" options={segments} value={segment} onChange={onSegmentChange} />
      )}

      <div className={s.toolbarActions}>
        <ButtonPill
          ref={setFilterButtonEl}
          aria-haspopup="dialog"
          aria-expanded={isFilterOpen}
          onClick={() => setIsFilterOpen((prev) => !prev)}
        >
          <BiFilterAlt aria-hidden="true" /> Filter
        </ButtonPill>
        {actions}
      </div>

      <AnchoredPopover
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        anchorEl={filterButtonEl}
        label="Filters"
        width={260}
      >
        <div className={s.filterMenu}>
          {hasActiveFilter && onClearFilters && (
            <button type="button" className={s.clearFilters} onClick={onClearFilters}>
              Clear Filters
            </button>
          )}
          {filterGroups.map((group) => (
            <div key={group.key} className={s.filterGroup}>
              <span className={s.filterGroupTitle}>{group.title}</span>
              <ul className={s.filterOptions}>
                {group.options.map((option) => {
                  const active = (filterValues[group.key] ?? 'all') === option.value;
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        className={`${s.filterOption} ${active ? s.filterOptionActive : ''}`}
                        onClick={() => onFilterChange(group.key, option.value)}
                      >
                        <span className={s.filterCheck}>
                          {active && <BiCheck aria-hidden="true" />}
                        </span>
                        {option.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </AnchoredPopover>
    </div>
  );
};

export default ExpensesToolbar;
